import React from 'react';
import styled from 'styled-components';
import { Body5, Container, H5 } from '../../libs/nvstr-common-ui.es';
import { ResultOnly } from '../../containers/DigitalAnalyst/components/AnswerLine';
import { SkeletonButton } from '../buttons/SkeletonButton';
import { ROUTES } from '../../constants/routes';

const Wrapper = styled.div``;
const HeaderWrapper = styled.div`
  padding: 8px 0 8px 0;

  h5,
  p {
    padding: 0 0 0 0;
    margin: 0 0 0 0;
  }
`;
const KeyValueTable = styled.table`
  border: none;
  width: 100%;

  tr {
  }

  td {
    &:first-child {
      width: 160px;
    }

    vertical-align: top;
    border-bottom: 1px solid ${({ theme }) => theme.themeColors.border};
  }
`;
const KVWrapper = styled.div`
  padding: 0 0 0 8px;

  p {
    margin: 0;
  }
`;
const SectionWrapper = styled.div`
  padding-top: 24px;
`;
const SectionHeading = styled.div`
  h5 {
    font-size: 16px !important;
  }
`;
const DealSnapshotWrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.border};

  ul {
    list-style: square;
    margin-bottom: 0;
  }

  li {
    padding: 0 0 16px 0;
  }
`;
const DealSnapshotHeadingWrapper = styled.div`
  background: #480a01;
  padding: 8px;
  text-align: center;

  * {
    color: #fff !important;
  }
`;

const KeyValueRowGroup = ({ children }) => {
  return (
    <KeyValueTable>
      <tbody>{children}</tbody>
    </KeyValueTable>
  );
};
const KeyValueRow = ({ keyName, templateKey, answers }) => {
  const answerValue = answers[templateKey] || 'Working...';
  return (
    <Container row verticallyCentered bottom={4}>
      <Body5 bold>{keyName}:</Body5>
      <KVWrapper>
        <ResultOnly result={answerValue} />
      </KVWrapper>
    </Container>
  );
};

const TextAnswer = ({ answers, templateKey }) => {
  const answerValue = answers[templateKey] || null;

  if (answerValue === null) return <Body5>Working...</Body5>;

  return <ResultOnly result={answerValue} />;
};

const ParagraphValue = ({ answers, templateKey, heading }) => {
  const answerValue = answers[templateKey] || 'Working...';
  return (
    <Container top={16}>
      <Container>
        <Body5 bold>{heading}</Body5>
      </Container>
      <Container top={16}>
        {templateKey === null ? <Container bottom={76} /> : <ResultOnly result={answerValue} />}
      </Container>
    </Container>
  );
};

export const InvestmentMemo = ({ answers, selectedDocs }) => {
  if (!answers) {
    return null;
  }

  const onQueryDocumentsClick = () => {
    const url = ROUTES.digitalAnalyst + `?doc-ids=${selectedDocs.map((d) => d.id).join(',')}`;
    window.open(url, '_blank');
  };

  return (
    <Wrapper>
      <HeaderWrapper>
        <Container row verticallyCenter spaceBetween>
          <H5>Confidential</H5>
          <Container left={8}>
            <SkeletonButton onClick={onQueryDocumentsClick}>Query Documents</SkeletonButton>
          </Container>
        </Container>
      </HeaderWrapper>

      <Container row top={24}>
        <KeyValueRowGroup>
          <KeyValueRow keyName={'Company Name'} answers={answers} templateKey={'{{COMPANY NAME}}'} />
          <KeyValueRow keyName={'Deal Lead'} answers={answers} templateKey={'{{DEAL LEAD}}'} />
          <KeyValueRow keyName={'Size'} answers={answers} templateKey={'{{NUMBER OF EMPLOYEES}}'} />
          <KeyValueRow keyName={'Headquarters'} answers={answers} templateKey={'{{HQ LOCATION}}'} />
          <KeyValueRow keyName={'Founder(s)'} answers={answers} templateKey={'{{NAMES OF FOUNDERS}}'} />
          <KeyValueRow keyName={'Investment size'} answers={answers} templateKey={'{{INVESTMENT SIZE}}'} />
          <KeyValueRow keyName={'Target round size'} answers={answers} templateKey={'{{TARGET ROUND SIZE}}'} />
          <KeyValueRow keyName={'Target ownership'} answers={answers} templateKey={'{{TARGET OWNERSHIP PERCENTAGE}}'} />
        </KeyValueRowGroup>
      </Container>

      <Container top={24}>
        <DealSnapshotWrapper>
          <DealSnapshotHeadingWrapper>
            <Body5 bold>Deal Snapshot</Body5>
          </DealSnapshotHeadingWrapper>
          <Container>
            <ul>
              <li>
                <TextAnswer answers={answers} templateKey={'{{DEAL SNAPSHOT}}'} />
              </li>
              <li>
                <TextAnswer answers={answers} templateKey={'{{DEAL DETAILS}}'} />
              </li>
            </ul>
          </Container>
        </DealSnapshotWrapper>
      </Container>

      <SectionWrapper>
        <SectionHeading>
          <H5>I. INVESTMENT OVERVIEW</H5>
        </SectionHeading>
        <ParagraphValue heading={'Source:'} answers={answers} templateKey={'{{INVESTMENT OVERVIEW: SOURCE}}'} />
        <ParagraphValue
          heading={'Company Description and Overview:'}
          answers={answers}
          templateKey={'{{INVESTMENT OVERVIEW: COMPANY DESCRIPTION}}'}
        />
        <ParagraphValue
          heading={'Deal Overview and Proposed Terms:'}
          answers={answers}
          templateKey={'{{INVESTMENT OVERVIEW: DEAL OVERVIEW}}'}
        />
        <ParagraphValue heading={'Team:'} answers={answers} templateKey={'{{INVESTMENT OVERVIEW: TEAM}}'} />
      </SectionWrapper>

      <SectionWrapper>
        <SectionHeading>
          <H5>II. INVESTMENT THESIS</H5>
        </SectionHeading>
        <ParagraphValue
          heading={'Thematic Overview '}
          answers={answers}
          templateKey={'{{INVESTMENT THESIS: THEMATIC OVERVIEW}}'}
        />
        <ParagraphValue heading={'Problem'} answers={answers} templateKey={'{{INVESTMENT THESIS: PROBLEM}}'} />
        <ParagraphValue heading={'Solution'} answers={answers} templateKey={'{{INVESTMENT THESIS: SOLUTION}}'} />
      </SectionWrapper>

      <SectionWrapper>
        <SectionHeading>
          <H5>III. TECHNOLOGY</H5>
        </SectionHeading>
        <ParagraphValue
          heading={'Product Overview '}
          answers={answers}
          templateKey={'{{TECHNOLOGY: PRODUCT OVERVIEW}}'}
        />
        <ParagraphValue heading={'Solution Stack'} answers={answers} templateKey={'{{TECHNOLOGY: STACK}}'} />
        <ParagraphValue
          heading={'Patents or Proprietary Technologies'}
          answers={answers}
          templateKey={'{{TECHNOLOGY: IP}}'}
        />
      </SectionWrapper>

      <SectionWrapper>
        <SectionHeading>
          <H5>IV. PRODUCT MARKET FIT VALIDATION</H5>
        </SectionHeading>
        <ParagraphValue heading={'Business Model:'} answers={answers} templateKey={'{{PMF: MODEL}}'} />
        <ParagraphValue heading={'Go-to-Market Strategy:'} answers={answers} templateKey={'{{PMF: GTM}}'} />
        <ParagraphValue heading={'Competition:'} answers={answers} templateKey={'{{PMF: COMPETITOR NAMES}}'} />
        <ParagraphValue heading={'Competitors:'} answers={answers} templateKey={'{{PMF: COMPETITORS}}'} />
      </SectionWrapper>

      <SectionWrapper>
        <SectionHeading>
          <H5>V. PERFORMANCE OVERVIEW</H5>
        </SectionHeading>
        <ParagraphValue heading={'Financials:'} answers={answers} templateKey={'{{PERFORMANCE: FINANCIALS}}'} />
        <ParagraphValue
          heading={'Customers and / or Users:'}
          answers={answers}
          templateKey={'{{PERFORMANCE: CUSTOMERS}}'}
        />
        <ParagraphValue heading={'Cash Position:'} answers={answers} templateKey={'{{PERFORMANCE: CASH POSITION}}'} />
      </SectionWrapper>

      <SectionWrapper>
        <SectionHeading>
          <H5>VI. DEAL DYNAMICS</H5>
        </SectionHeading>
        <ParagraphValue
          heading={'Previous Funding: '}
          answers={answers}
          templateKey={'{{DEAL DYNAMICS: PREVIOUS FUNDING}}'}
        />
        <ParagraphValue heading={'Valuation:'} answers={answers} templateKey={null} />
        <ParagraphValue
          heading={'Use of Proceeds:'}
          answers={answers}
          templateKey={'{{DEAL DYNAMICS: USE OF PROCEEDS}}'}
        />
        <ParagraphValue heading={'Return Model:'} answers={answers} templateKey={null} />
        <ParagraphValue heading={'Exit potential:'} answers={answers} templateKey={null} />
        <ParagraphValue heading={'Exhibit XX:'} answers={answers} templateKey={null} />
      </SectionWrapper>

      <SectionWrapper>
        <SectionHeading>
          <H5>VII. ADDITIONAL CONSIDERATIONS</H5>
        </SectionHeading>

        <ParagraphValue heading={'Value Add:'} answers={answers} templateKey={null} />
        <ParagraphValue
          heading={'Key Risks & Mitigants: '}
          answers={answers}
          templateKey={'{{KEY RISKS AND MITIGANTS}}'}
        />
      </SectionWrapper>

      <SectionWrapper>
        <SectionHeading>
          <H5>APPENDIX</H5>
        </SectionHeading>

        <ParagraphValue
          heading={'Customer Interviews'}
          answers={answers}
          templateKey={'{{APPENDIX: CUSTOMER INTERVIEWS}}'}
        />
        <ParagraphValue
          heading={'Investor Interviews'}
          answers={answers}
          templateKey={'{{APPENDIX: INVESTOR INTERVIEWS}}'}
        />
        <ParagraphValue
          heading={'Expert Interviews'}
          answers={answers}
          templateKey={'{{APPENDIX: EXPERT INTERVIEWS}}'}
        />
        <ParagraphValue heading={'Additional market research or data'} answers={answers} templateKey={null} />
      </SectionWrapper>
    </Wrapper>
  );
};
